import { Article } from '@components/article/Article/Article';
import { NextArticles } from '@components/article/NextArticles/NextArticles';
import { Button } from '@components/common/Button';
import { Seo } from '@components/common/Seo';
import { ArticleJsonLd } from '@components/common/Seo/JsonLd/ArticleJsonLd';
import { NewsArticleJsonLd } from '@components/common/Seo/JsonLd/NewsArticleJsonLd';
import { socials } from '@consts/footer';
import { urls } from '@consts/images';
import { useNextArticles } from '@queries/useNextArticles';
import { countWordsInArticleContent } from '@utils/countWordsInArticleContent';
import { buildArticleLink } from '@utils/link';
import { buildArticleOpenGraph, buildArticleOpenGraphImage } from '@utils/seo';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import tw from 'twin.macro';
import { useArticleData } from './ArticlePage.hooks';
import { IArticlePage } from './ArticlePage.types';
import { LoadMoreBtnContainer } from './styles';

export const ArticlePage: FC<IArticlePage> = ({
  id,
  seo,
  articleSlug,
  isPreview,
  ads,
  affiliateLinks,
  affiliateLinksHeading,
  topReads,
  latestNews,
  robotsIndex,
}) => {
  const article = useArticleData({ id, slug: articleSlug, isPreview, isEnabled: isPreview });

  const router = useRouter();
  const { t } = useTranslation('articles');
  const queryClient = useQueryClient();
  const defaultLoadMoreArticlesParams = useMemo(
    () => ({
      published_at_lte: article?.publishedAt,
      id_nin: [Number(id)],
      categories_in: [article?.categories[0]?.id],
      _sort: 'published_at:DESC',
    }),
    [article, id],
  );

  const [loadMoreArticlesParams, setLoadMoreArticlesParams] = useState(
    defaultLoadMoreArticlesParams,
  );

  const { fetchNextPage, data, totalPages, isFetchingNextPage } = useNextArticles(
    {
      published_at_lte: article?.publishedAt,
      id_nin: [article?.id],
    },
    defaultLoadMoreArticlesParams,
  );
  const url = buildArticleLink(article?.id, article?.slug);
  const canLoadMore = totalPages !== data?.pages?.length && data?.pages?.length % 2 === 0;
  const hasMoreArticles = data?.pages?.length > 0;

  const nextArticles = useMemo(
    () => (hasMoreArticles ? data.pages.map(({ data: nextArticle }) => nextArticle) : []),
    [data, hasMoreArticles],
  );

  useEffect(() => {
    queryClient.resetQueries(['next-articles']);
    setLoadMoreArticlesParams(defaultLoadMoreArticlesParams);
  }, [defaultLoadMoreArticlesParams, queryClient]);

  const onReachArticleEnd = useCallback(
    async (articleId) => {
      if (!totalPages) {
        return;
      }

      if (!data?.pages?.length) {
        await fetchNextPage();
        return;
      }

      const nextPageLoadMoreArticlesParams = {
        ...loadMoreArticlesParams,
        id_nin: [...loadMoreArticlesParams.id_nin.map((excludedId) => excludedId), articleId],
      };

      if (totalPages === data?.pages?.length || data?.pages?.length % 2 === 0) {
        setLoadMoreArticlesParams(nextPageLoadMoreArticlesParams);
        return;
      }

      await fetchNextPage({ pageParam: nextPageLoadMoreArticlesParams });
      setLoadMoreArticlesParams(nextPageLoadMoreArticlesParams);
    },
    [data, fetchNextPage, loadMoreArticlesParams, totalPages],
  );
  const onChangeArticleUrl = useCallback(
    async (articleId, slug) => {
      const newPath = `/${articleId}/${slug}`;

      if (router.asPath !== newPath) {
        await router.push(newPath, null, { shallow: true });
      }
    },
    [router],
  );

  const isInCategory = useCallback(
    (categoryName: string) => {
      return !!article.categories.filter((category) => category.name === categoryName).length;
    },
    [article.categories],
  );

  const onLoadNextArticle = async () => {
    await fetchNextPage({ pageParam: loadMoreArticlesParams });
  };

  const image = buildArticleOpenGraphImage({
    shareImage: seo?.shareImage,
    articleThumbnail: article?.thumbnail,
  });

  if (!article) {
    return null;
  }

  const isNews = isInCategory('News');

  const category = article.subcategories[0]?.name;

  const tags = article?.tags?.map(({ name }) => name);

  const contentWordCount = countWordsInArticleContent(
    article.content ? JSON.parse(article.content) : [],
    0,
  );

  return (
    <>
      <Seo
        canonical={url}
        siteName={seo.siteName}
        metaTitle={seo.metaTitle}
        metaDescription={seo.metaDescription}
        openGraph={buildArticleOpenGraph({
          title: seo.shareTitle,
          description: seo.shareDescription,
          sitename: seo.siteName,
          url,
          tags,
          modifiedTime: article?.updatedAt,
          publishedTime: article?.publishedDate ?? article.publishedAt,
          image,
        })}
        titleTemplate="%s"
        noindex={!robotsIndex}
      />

      {isNews ? (
        <NewsArticleJsonLd
          url={url}
          thumbnail={article.thumbnail}
          abstract={article.description}
          title={seo.shareTitle}
          images={[image.url]}
          section={category}
          keywords={tags.join(',')}
          datePublished={article?.publishedDate ?? article.publishedAt}
          dateModified={article?.updatedAt}
          dateCreated={article?.publishedDate ?? article.publishedAt}
          author={article?.author}
          publisher={{
            name: 'Coinpaper',
            url: 'https://coinpaper.com',
            socials: Object.values(socials),
            logo: {
              url: urls.logo,
              width: 399,
              height: 83,
            },
          }}
          seoDescription={seo.shareDescription}
          wordCount={contentWordCount}
        />
      ) : (
        <ArticleJsonLd
          url={url}
          thumbnail={article.thumbnail}
          abstract={article.description}
          title={seo.shareTitle}
          seoDescription={seo.shareDescription}
          images={[image.url]}
          datePublished={article?.publishedDate ?? article.publishedAt}
          dateModified={article?.updatedAt}
          keywords={tags.join(',')}
          author={article?.author}
          publisher={{
            name: 'Coinpaper',
            url: 'https://coinpaper.com',
            socials: Object.values(socials),
            logo: {
              url: urls.logo,
              width: 399,
              height: 83,
            },
          }}
          wordCount={contentWordCount}
        />
      )}
      <Article
        divider={totalPages > 0}
        key={String(id)}
        {...article}
        url={url}
        onEndReach={onReachArticleEnd}
        onArticleVisible={onChangeArticleUrl}
        ads={ads}
        affiliateLinks={affiliateLinks}
        affiliateLinksHeading={affiliateLinksHeading}
        topReads={topReads}
        latestNews={latestNews}
      />
      {hasMoreArticles && (
        <NextArticles
          articles={nextArticles}
          canLoadMore={canLoadMore}
          onArticleVisible={onChangeArticleUrl}
          onReachArticleEnd={onReachArticleEnd}
          ads={ads}
        />
      )}
      {canLoadMore && (
        <LoadMoreBtnContainer>
          <Button
            text={t('load-more')}
            css={tw`mx-auto mb-8`}
            onClick={onLoadNextArticle}
            loading={isFetchingNextPage}
          />
        </LoadMoreBtnContainer>
      )}
    </>
  );
};
